import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Timer, ChevronDown, Search } from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectCompanyName } from '@/store/slices/companySlice';
import { selectActiveCompanyId } from '@/store/slices/workspaceSlice';
import { useMonthlyAllAttendance } from '@/api/queries/attendance.queries';
import { useDepartments, useEmployees } from '@/api/queries/employee.queries';
import { StatCard } from '@/components/shared/StatCard';
import { ReportExportBar } from '@/components/reports/ReportExportBar';
import { buildOTRows } from '@/lib/ReportGenerator';
import { cn } from '@/lib/cn';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function fmtMins(min) {
  if (!min || min <= 0) return '0h 0m';
  return `${Math.floor(min / 60)}h ${min % 60}m`;
}

export default function OTReportPage() {
  const companyName     = useSelector(selectCompanyName);
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const now = new Date();
  const [year, setYear]     = useState(now.getFullYear());
  const [month, setMonth]   = useState(now.getMonth() + 1);
  const [deptFilter, setDept]     = useState('');
  const [minOT, setMinOT]         = useState(0); // minimum OT minutes filter
  const [search, setSearch]       = useState('');
  const [otRate, setOtRate]       = useState(100); // ₹/hr
  const [expandedId, setExpanded] = useState(null);

  const monthStr = `${year}-${String(month).padStart(2, '0')}`;

  // Real employees from the running service — drives the generated data
  const { data: empData }  = useEmployees({ status: 'ACTIVE', limit: 500 });
  const realEmployees = useMemo(() => empData?.data || [], [empData]);

  const { data: deptData }  = useDepartments();
  const departments = deptData?.data || [];

  const selectedDeptName = departments.find(d => d.id === deptFilter)?.name || '';
  const { data, isLoading } = useMonthlyAllAttendance(monthStr, selectedDeptName ? { department: selectedDeptName } : {}, activeCompanyId, realEmployees);

  const employees = data?.data || [];

  // Compute per-employee OT totals from `times`
  const otEmployees = useMemo(() => {
    return employees.map(emp => {
      let totalOtMin = 0;
      let otDays = 0;
      const dailyOT = {};

      if (emp.times) {
        Object.entries(emp.times).forEach(([day, t]) => {
          if (!t) return;  // null for weekends/absent days returned by backend
          const otMin = t.ot_min || 0;
          if (otMin > 0) {
            totalOtMin += otMin;
            otDays++;
            dailyOT[day] = { ...t, ot_min: otMin };
          }
        });
      }
      // Fallback: parse summary.OT string "3h 20m"
      if (totalOtMin === 0 && emp.summary?.OT && emp.summary.OT !== '0h 0m') {
        const m = emp.summary.OT.match(/(\d+)h\s*(\d+)m/);
        if (m) totalOtMin = parseInt(m[1]) * 60 + parseInt(m[2]);
        if (totalOtMin > 0) otDays = emp.summary.P || 1;
      }

      const avgOtMin = otDays ? Math.round(totalOtMin / otDays) : 0;
      const estPay   = Math.round((totalOtMin / 60) * otRate);

      return {
        ...emp,
        totalOtMin,
        otDays,
        avgOtMin,
        estPay,
        dailyOT,
      };
    })
    .filter(e => e.totalOtMin >= minOT)
    .filter(e => !search || e.name?.toLowerCase().includes(search.toLowerCase()) || e.employee_code?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => b.totalOtMin - a.totalOtMin);
  }, [employees, minOT, search, otRate]);

  // Grand totals
  const grandTotals = useMemo(() => ({
    totalOtMin: otEmployees.reduce((s, e) => s + e.totalOtMin, 0),
    withOT:     otEmployees.filter(e => e.totalOtMin > 0).length,
    maxOtMin:   Math.max(0, ...otEmployees.map(e => e.totalOtMin)),
    avgOtMin:   otEmployees.length ? Math.round(otEmployees.reduce((s, e) => s + e.totalOtMin, 0) / otEmployees.length) : 0,
    estPay:     otEmployees.reduce((s, e) => s + e.estPay, 0),
  }), [otEmployees]);

  const exportRows = useMemo(() => buildOTRows(otEmployees), [otEmployees]);

  return (
    <div className="p-6 space-y-5">
      <nav className="flex items-center gap-1.5 text-[12px] text-[var(--text-tertiary)]">
        <Link to="/reports" className="hover:text-[var(--accent)]">Reports</Link>
        <ChevronRight size={12}/>
        <span className="text-[var(--text-primary)] font-medium">OT Report</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">OT Report</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">{MONTHS[month-1]} {year} · {companyName}</p>
        </div>
        <ReportExportBar rows={exportRows} filename={`ot-${monthStr}`} title="OT Report" companyName={companyName} filterSummary={`${MONTHS[month-1]} ${year}`}/>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total OT Hours"   value={fmtMins(grandTotals.totalOtMin)} icon={Timer} color="blue"/>
        <StatCard title="Employees w/ OT"  value={grandTotals.withOT.toString()}   icon={Timer} color="green"/>
        <StatCard title="Max OT (single)"  value={fmtMins(grandTotals.maxOtMin)}   icon={Timer} color="yellow"/>
        <StatCard title="Est. OT Pay"      value={`₹${grandTotals.estPay.toLocaleString('en-IN')}`} icon={Timer} color="purple"/>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        <select value={month} onChange={e => setMonth(Number(e.target.value))}
          className="h-9 px-3 text-[13px] bg-[var(--bg-card)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
        </select>
        <select value={year} onChange={e => setYear(Number(e.target.value))}
          className="h-9 px-3 text-[13px] bg-[var(--bg-card)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={deptFilter} onChange={e => setDept(e.target.value)}
          className="h-9 px-3 text-[13px] bg-[var(--bg-card)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <div className="flex items-center gap-1.5 h-9 px-3 border border-[var(--border)] rounded-lg bg-[var(--bg-card)]">
          <Search size={13} className="text-[var(--text-tertiary)]"/>
          <input placeholder="Search employee…" value={search} onChange={e => setSearch(e.target.value)}
            className="w-32 text-[13px] bg-transparent outline-none placeholder:text-[var(--text-tertiary)]"/>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-[12px] text-[var(--text-secondary)]">OT Rate ₹/hr</span>
          <input type="number" value={otRate} onChange={e => setOtRate(Number(e.target.value))} min={0} step={10}
            className="w-20 h-9 px-2.5 text-[13px] text-right bg-[var(--bg-card)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]"/>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-[var(--text-secondary)]">Min OT (min)</span>
          <input type="number" value={minOT} onChange={e => setMinOT(Number(e.target.value))} min={0} step={15}
            className="w-20 h-9 px-2.5 text-[13px] text-right bg-[var(--bg-card)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]"/>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--bg-app)]">
                {['Employee','Code','Department','Designation','OT Days','Total OT','Avg OT/Day',`OT Pay (₹${otRate}/hr)`,''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-[var(--border)]">
                    {Array.from({ length: 9 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 bg-[var(--bg-hover)] rounded animate-pulse w-20"/></td>
                    ))}
                  </tr>
                ))
              ) : otEmployees.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-[var(--text-tertiary)]">
                    <Timer size={32} className="mx-auto mb-2 opacity-30"/>
                    No overtime data for the selected period
                  </td>
                </tr>
              ) : otEmployees.map(emp => (
                <React.Fragment key={emp.employee_id}>
                  <tr
                    className={cn('border-b border-[var(--border)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer', expandedId === emp.employee_id && 'bg-[var(--bg-hover)]')}
                    onClick={() => setExpanded(v => v === emp.employee_id ? null : emp.employee_id)}>
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)] whitespace-nowrap">{emp.name}</td>
                    <td className="px-4 py-3 font-mono text-[12px] text-[var(--text-tertiary)]">{emp.employee_code}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{emp.department}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{emp.designation || '—'}</td>
                    <td className="px-4 py-3 font-medium text-center">{emp.otDays}</td>
                    <td className="px-4 py-3 font-bold text-[var(--accent)] tabular-nums">{fmtMins(emp.totalOtMin)}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] tabular-nums">{fmtMins(emp.avgOtMin)}</td>
                    <td className="px-4 py-3 font-semibold text-green-600">₹{emp.estPay.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <ChevronDown size={14} className={cn('text-[var(--text-tertiary)] transition-transform', expandedId === emp.employee_id && 'rotate-180')}/>
                    </td>
                  </tr>
                  {expandedId === emp.employee_id && Object.keys(emp.dailyOT).length > 0 && (
                    <tr className="border-b border-[var(--border)]">
                      <td colSpan={9} className="px-8 py-3 bg-[var(--bg-app)]">
                        <p className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-2">Daily OT Breakdown</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(emp.dailyOT).sort(([a], [b]) => Number(a) - Number(b)).map(([day, t]) => (
                            <div key={day} className="flex items-center gap-2 px-3 py-1.5 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-[12px]">
                              <span className="font-medium text-[var(--text-secondary)]">Day {day}</span>
                              <span className="text-[var(--accent)] font-bold">{fmtMins(t.ot_min)}</span>
                              {t.out && <span className="text-[var(--text-tertiary)]">out {t.out}</span>}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {/* Totals row */}
              {otEmployees.length > 0 && (
                <tr className="border-t-2 border-[var(--border)] bg-[var(--bg-app)] font-bold">
                  <td className="px-4 py-3" colSpan={4}>Grand Total</td>
                  <td className="px-4 py-3 text-center">{grandTotals.withOT}</td>
                  <td className="px-4 py-3 text-[var(--accent)]">{fmtMins(grandTotals.totalOtMin)}</td>
                  <td className="px-4 py-3 text-[var(--text-secondary)]">{fmtMins(grandTotals.avgOtMin)}</td>
                  <td className="px-4 py-3 text-green-600">₹{grandTotals.estPay.toLocaleString('en-IN')}</td>
                  <td/>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
